import React, { useEffect, useState } from 'react';
import { Table, Card, Tag, Select } from 'antd';
import { getRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

interface InvoiceUsageHistory {
  id: number;
  schoolId: number;
  year: number;
  invoiceSymbol: string;
  type: number; // 1: Add, 2: Use
  quantity: number;
  remainingQuantity: number;
  totalPurchased: number;
  note: string;
  createdAt: number[];
}

interface InvoiceUsageSummary {
  remainingQuantity: number;
  totalPurchased: number;
}

export const InvoiceHistory: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<InvoiceUsageHistory[]>([]);
  const [summary, setSummary] = useState<InvoiceUsageSummary | null>(null);
  const [invoiceSymbol, setInvoiceSymbol] = useState<string>('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const schoolId = localStorage.getItem('schoolId');
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 1 }, (_, i) => currentYear - i);

  const fetchHistory = async (symbol?: string) => {
    if (!schoolId) return;
    const currentSymbol = symbol || invoiceSymbol;
    if (!currentSymbol) return;

    setLoading(true);
    try {
      const res: ResponseDataType<InvoiceUsageHistory[]> = await getRequest(
        `/cms/school-bill-config/invoice-usage-history?schoolId=${schoolId}&year=${year}&invoiceSymbol=${currentSymbol}`
      );
      if (res?.code === 200 && Array.isArray(res.info)) {
        setHistory(res.info);
      } else {
        setHistory([]);
      }
    } catch (error) {
      toast.error('Lỗi khi tải lịch sử hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    if (!schoolId) return;
    try {
      const configRes: ResponseDataType<any> = await getRequest(`/cms/school-bill-config?schoolId=${schoolId}`);
      if (configRes?.code === 200 && configRes.info) {
        const config = configRes.info;
        const sym = config.symbol || '';
        setInvoiceSymbol(sym);
        if (sym) {
          fetchHistory(sym);
          const summaryRes: ResponseDataType<InvoiceUsageSummary> = await getRequest(
            `/cms/school-bill-config/invoice-usage-summary?schoolId=${schoolId}&year=${year}&invoiceSymbol=${sym}`
          );
          if (summaryRes?.code === 200 && summaryRes.info) {
            setSummary(summaryRes.info);
          } else {
            setSummary({ remainingQuantity: 0, totalPurchased: 0 });
          }
        }
      } else {
        setInvoiceSymbol('');
        setSummary({ remainingQuantity: 0, totalPurchased: 0 });
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummary({ remainingQuantity: 0, totalPurchased: 0 });
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [schoolId, year]);

  const columns = [
    {
      title: 'Ngày thực hiện',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: number[]) => {
        if (!date) return '';
        const [year, month, day, hour = 0, minute = 0] = date;
        return dayjs(new Date(Date.UTC(year, month - 1, day, hour, minute))).format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'Ký hiệu',
      dataIndex: 'invoiceSymbol',
      key: 'invoiceSymbol',
    },
    {
      title: 'Loại thao tác',
      dataIndex: 'type',
      key: 'type',
      render: (type: number) => (
        <Tag color={type === 1 ? 'green' : 'blue'}>
          {type === 1 ? 'Thêm mới' : 'Sử dụng'}
        </Tag>
      ),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (val: number, record: any) => (
        <span style={{ color: record.type === 1 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }}>
          {record.type === 1 ? '+' : '-'}{val}
        </span>
      ),
    },
    {
      title: 'Còn lại',
      dataIndex: 'remainingQuantity',
      key: 'remainingQuantity',
      render: (val: number) => <b>{val}</b>,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* Thanh bộ lọc và 2 card thống kê nhỏ gọn cùng 1 dòng */}
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* Bộ lọc Năm & Ký hiệu */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontWeight: 500, fontSize: 13, color: '#475569' }}>Năm:</span>
          <Select
            value={year}
            onChange={(val) => setYear(val)}
            style={{ width: 105 }}
            size="small"
            options={years.map((y) => ({ label: `Năm ${y}`, value: y }))}
          />
          {invoiceSymbol ? (
            <span style={{ color: '#64748b', fontSize: 13, marginLeft: '6px' }}>
              | Ký hiệu: <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>{invoiceSymbol}</Tag>
            </span>
          ) : null}
        </div>

        {/* 2 Card thống kê nhỏ gọn */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <Card
            bordered={false}
            style={{
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              borderRadius: '6px',
              border: '1px solid #f0f2f5',
            }}
            bodyStyle={{ padding: '6px 14px' }}
            styles={{ body: { padding: '6px 14px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Tổng số hóa đơn đã mua:
              </span>
              <span style={{ color: '#1890ff', fontSize: 16, fontWeight: 700 }}>
                {(summary?.totalPurchased || 0).toLocaleString('vi-VN')}
              </span>
            </div>
          </Card>

          <Card
            bordered={false}
            style={{
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              borderRadius: '6px',
              border: '1px solid #f0f2f5',
            }}
            bodyStyle={{ padding: '6px 14px' }}
            styles={{ body: { padding: '6px 14px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                Số lượng hóa đơn còn lại:
              </span>
              <span
                style={{
                  color: summary?.remainingQuantity && summary.remainingQuantity < 50 ? '#cf1322' : '#3f8600',
                  fontSize: 16,
                  fontWeight: 700,
                }}
              >
                {(summary?.remainingQuantity || 0).toLocaleString('vi-VN')}
              </span>
            </div>
          </Card>
        </div>
      </div>

      {/* Table giữ nguyên 100% như cũ */}
      <Card
        title="Chi tiết lịch sử"
        bordered={false}
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '12px' }}
      >
        <Table
          columns={columns}
          dataSource={history}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  );
};
